import { Dialog, Transition } from '@headlessui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { IoAddOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { ApprovalFlowTypeEnum } from '@/constants/enums/ApprovalFlowTypeEnum';
import api from '@/services/apiSgft';
import { Button } from '../atoms/Button';
import CloseButton from '../atoms/CloseButton';
import ControlledSimpleSelect from '../atoms/ControlledSimpleSelect';
import SubtitleText from '../atoms/SubtitleText';
import Subtitle from '../molecules/Subtitle';
import { ApprovalStep } from './ApprovalFlowModal/components/ApprovalStep';
import { RequesterItem } from './ApprovalFlowModal/components/RequesterItem';
import { useCompanyMapping } from './ApprovalFlowModal/hooks/useApprovalFlowData';
import {
	ApprovalFlowData,
	ApprovalFlowFormData,
} from './ApprovalFlowModal/types';

export default function ApprovalFlowModal({
	isOpen,
	onClose,
	data,
}: {
	isOpen: boolean;
	onClose: () => void;
	data?: ApprovalFlowData;
}) {
	const { control, handleSubmit, watch, setValue, reset } =
		useForm<ApprovalFlowFormData>({
			defaultValues: {
				companyId: 0,
				boardId: 0,
				managementId: 0,
				type: ApprovalFlowTypeEnum.Regular,
				requesters: [0],
				steps: [
					{
						step: 1,
						approvers: [0],
					},
				],
			},
		});

	const queryClient = useQueryClient();
	const requesters = watch('requesters');
	const steps = watch('steps');
	const companyId = watch('companyId');
	const boardId = watch('boardId');
	const managementId = watch('managementId');
	const isInitialLoadRef = useRef(true);
	const initialCompanyIdRef = useRef<number | null>(null);
	const initialBoardIdRef = useRef<number | null>(null);

	useEffect(() => {
		if (!isOpen) {
			isInitialLoadRef.current = true;
			initialCompanyIdRef.current = null;
			initialBoardIdRef.current = null;
			return;
		}

		if (data) {
			initialCompanyIdRef.current = data.companyId;
			initialBoardIdRef.current = data.boardId;
			reset({
				companyId: data.companyId,
				boardId: data.boardId,
				managementId: data.managementId,
				type: data.type,
				requesters: data.requesters.map(
					(r: { employeeId: number; name: string }) => r.employeeId,
				),
				steps: data.approvalSteps.map(
					(step: {
						step: number;
						approvers: Array<{ employeeId: number; name: string }>;
					}) => ({
						step: step.step,
						approvers: step.approvers.map(
							(a: { employeeId: number; name: string }) => a.employeeId,
						),
					}),
				),
			});
		} else {
			initialCompanyIdRef.current = null;
			initialBoardIdRef.current = null;
			reset({
				companyId: 0,
				boardId: 0,
				managementId: 0,
				type: ApprovalFlowTypeEnum.Regular,
				requesters: [0],
				steps: [{ step: 1, approvers: [0] }],
			});
		}

		setTimeout(() => {
			isInitialLoadRef.current = false;
		}, 100);
	}, [data, isOpen, reset]);

	const { data: companyMapping, isLoading: isLoadingMapping } =
		useCompanyMapping();

	const companyOptions =
		companyMapping?.companies?.map((company: { id: number; name: string }) => ({
			value: company.id,
			label: company.name,
		})) || [];

	const selectedCompany = companyMapping?.companies?.find(
		(company: { id: number; name: string }) => company.id === companyId,
	);

	const boardOptions =
		selectedCompany?.boards?.map((board: { id: number; name: string }) => ({
			value: board.id,
			label: board.name,
		})) || [];

	const selectedBoard = selectedCompany?.boards?.find(
		(board: { id: number; name: string }) => board.id === boardId,
	);

	const managementOptions =
		selectedBoard?.managements?.map(
			(management: { id: number; name: string }) => ({
				value: management.id,
				label: management.name,
			}),
		) || [];

	useEffect(() => {
		if (isInitialLoadRef.current) return;

		if (
			companyId &&
			companyId !== 0 &&
			companyId !== initialCompanyIdRef.current
		) {
			setValue('boardId', 0);
			setValue('managementId', 0);
			initialBoardIdRef.current = null;
		}
	}, [companyId, setValue]);

	useEffect(() => {
		if (isInitialLoadRef.current) return;

		if (boardId && boardId !== 0 && boardId !== initialBoardIdRef.current) {
			setValue('managementId', 0);
		}
	}, [boardId, setValue]);

	const addRequester = () => {
		setValue('requesters', [...requesters, 0]);
	};

	const removeRequester = (index: number) => {
		setValue(
			'requesters',
			requesters.filter((_: number, i: number) => i !== index),
		);
	};

	const addLevel = () => {
		setValue('steps', [...steps, { step: steps.length + 1, approvers: [0] }]);
	};

	const removeLevel = (levelIndex: number) => {
		const newLevels = steps.filter(
			(_: { step: number; approvers: number[] }, i: number) => i !== levelIndex,
		);
		const adjustedSteps = newLevels.map(
			(step: { step: number; approvers: number[] }, index: number) => ({
				...step,
				step: index + 1,
			}),
		);
		setValue('steps', adjustedSteps);
	};

	const addApprover = (levelIndex: number) => {
		const newSteps = [...steps];
		newSteps[levelIndex] = {
			...newSteps[levelIndex],
			approvers: [...newSteps[levelIndex].approvers, 0],
		};
		setValue('steps', newSteps);
	};

	const removeApprover = (levelIndex: number, approverIndex: number) => {
		const newSteps = [...steps];
		newSteps[levelIndex] = {
			...newSteps[levelIndex],
			approvers: newSteps[levelIndex].approvers.filter(
				(_: number, i: number) => i !== approverIndex,
			),
		};
		setValue('steps', newSteps);
	};

	const prepareSubmitData = (formData: ApprovalFlowFormData) => ({
		managementId: formData.managementId,
		type: formData.type,
		requesters: formData.requesters.map((id: number) => Number(id)),
		steps: formData.steps.map(
			(step: { step: number; approvers: number[] }) => ({
				step: Number(step.step),
				approvers: step.approvers.map((id: number) => Number(id)),
			}),
		),
	});

	const saveApprovalFlowMutation = useMutation({
		mutationFn: async (formData: ApprovalFlowFormData) => {
			const submitData = prepareSubmitData(formData);
			const endpoint = data?.id
				? `/approval/flow/${data.id}`
				: '/approval/flow';
			const method = data?.id ? 'put' : 'post';
			return await api[method](endpoint, submitData);
		},
		onSuccess: () => {
			const message = data?.id
				? 'Fluxo de aprovação atualizado com sucesso!'
				: 'Fluxo de aprovação salvo com sucesso!';
			toast.success(message);
			queryClient.invalidateQueries({
				queryKey: ['extra-trips-approval-flows'],
			});
			onClose();
		},
		onError: (error) => {
			const defaultMessage = data?.id
				? 'Erro ao atualizar fluxo de aprovação!'
				: 'Erro ao salvar fluxo de aprovação!';

			if (error instanceof AxiosError && error.response?.data?.message) {
				toast.error(error.response.data.message);
			} else {
				toast.error(defaultMessage);
			}
		},
	});

	const onSubmit = (formData: ApprovalFlowFormData) => {
		const stepsWithoutApprovers = formData.steps.filter(
			(step: { approvers: number[] }) => step.approvers.length === 0,
		);

		if (stepsWithoutApprovers.length > 0) {
			toast.error(
				'Todos os níveis de aprovação devem ter pelo menos um colaborador associado.',
			);
			return;
		}

		saveApprovalFlowMutation.mutate(formData);
	};

	const FLOW_TYPE_OPTIONS = [
		{ value: ApprovalFlowTypeEnum.Regular, label: 'REGULAR' },
		{ value: ApprovalFlowTypeEnum.Emergency, label: 'EMERGENCIAL' },
	];

	const isEditMode = !!data?.id;

	return (
		<Transition appear show={isOpen}>
			<Dialog className="relative z-50" onClose={onClose} open={true}>
				<div className="fixed inset-0 flex w-screen items-center justify-center bg-black/30 p-4">
					<Transition.Child
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<Dialog.Panel className="custom-scrollbar max-h-[calc(100vh-2rem)] w-[52rem] flex-col justify-between overflow-auto overflow-y-scroll rounded-lg bg-white">
							<form
								onSubmit={handleSubmit(onSubmit)}
								className="w-full flex-col justify-between p-5"
							>
								<div className="flex h-auto items-center justify-between">
									<div className="flex justify-between">
										<SubtitleText
											className="text-[20px]"
											subtitle={'Cadastramento de Aprovadores de Idas Extras'}
										/>
									</div>
									<CloseButton onClick={onClose} className="mt-[-1rem]" />
								</div>
								<div className="mt-5">
									<div className="mb-5 grid grid-cols-2 grid-rows-2 gap-4">
										<ControlledSimpleSelect
											width="100%"
											title="Empresa"
											values={companyOptions}
											control={control}
											name="companyId"
											className="flex-1"
											disabled={isEditMode || isLoadingMapping}
											required
										/>
										<ControlledSimpleSelect
											width="100%"
											title="Diretoria"
											values={boardOptions}
											control={control}
											name="boardId"
											className="flex-1"
											disabled={isEditMode || !companyId || isLoadingMapping}
											required
										/>
										<ControlledSimpleSelect
											width="100%"
											title="Gerência"
											values={managementOptions}
											control={control}
											name="managementId"
											className="flex-1"
											disabled={isEditMode || !boardId || isLoadingMapping}
											required
										/>
										<ControlledSimpleSelect
											width="100%"
											title="Tipo de fluxo"
											values={FLOW_TYPE_OPTIONS}
											control={control}
											name="type"
											className="flex-1"
											disabled={isEditMode}
											required
										/>
									</div>
								</div>
								<section>
									<Subtitle subtitle="Solicitantes" className="w-full" />
									<div className="flex flex-col gap-2">
										{(requesters || []).map(
											(requester: number, requesterIndex: number) => (
												<RequesterItem
													key={`requester-${requester}`}
													requesterIndex={requesterIndex}
													control={control}
													onRemove={() => removeRequester(requesterIndex)}
												/>
											),
										)}
										<Button
											type="button"
											variant={'elevated'}
											className="px-4 py-0 text-[0.75rem] w-fit h-7"
											onClick={addRequester}
										>
											<IoAddOutline size={16} />
											Novo colaborador
										</Button>
									</div>
								</section>
								<section className="mt-[20px]">
									<Subtitle subtitle="Níveis de aprovação" className="w-full" />
									<div className="flex flex-col gap-2">
										{(steps || []).map(
											(
												step: { step: number; approvers: number[] },
												stepIndex: number,
											) => (
												<ApprovalStep
													key={`step-${step.step}`}
													stepIndex={stepIndex}
													stepNumber={step.step}
													control={control}
													approvers={step.approvers}
													onAddApprover={() => addApprover(stepIndex)}
													onRemoveApprover={(approverIndex: number) =>
														removeApprover(stepIndex, approverIndex)
													}
													onRemoveLevel={() => removeLevel(stepIndex)}
												/>
											),
										)}
										<Button
											type="button"
											variant={'elevated'}
											className="px-4 py-0 text-[0.75rem] w-fit h-7"
											onClick={addLevel}
										>
											<IoAddOutline size={16} />
											Novo nível
										</Button>
									</div>
								</section>
								<div className="flex justify-between items-center mt-[30px]">
									<Button
										type="button"
										variant={'outline'}
										className="w-[190px] h-8"
										onClick={onClose}
									>
										Cancelar
									</Button>
									<Button
										type="submit"
										variant={'default'}
										className="w-[260px] h-8"
									>
										Salvar
									</Button>
								</div>
							</form>
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	);
}
